package songwritersnotebook.app.crdt

import songwritersnotebook.app.data.*
import org.khronos.webgl.Uint8Array
import org.khronos.webgl.get
import kotlinx.serialization.json.Json
import kotlinx.serialization.builtins.ListSerializer

private val json = Json { ignoreUnknownKeys = true }

@JsModule("@automerge/automerge")
@JsNonModule
external object Automerge {
    fun init(): dynamic
    fun change(doc: dynamic, message: String, callback: (dynamic) -> Unit): dynamic
    fun save(doc: dynamic): Uint8Array
    fun load(data: Uint8Array): dynamic
    fun applyChanges(doc: dynamic, changes: Array<Uint8Array>): Array<dynamic>
    fun getChanges(oldDoc: dynamic, newDoc: dynamic): Array<Uint8Array>
    fun merge(localDoc: dynamic, remoteDoc: dynamic): dynamic
    fun getHeads(doc: dynamic): Array<Uint8Array>
    fun getActorId(doc: dynamic): String
    val next: AutomergeNext

    class Text(text: String = definedExternally)
}

external interface AutomergeNext {
    fun splice(doc: dynamic, path: Array<Any>, index: Int, delCount: Int, newText: String): dynamic
}


@JsName("Object")
external object JsObject {
    fun keys(obj: Any?): dynamic
}

private fun dynamicToInt(dynProp: dynamic): Int? {
    if (dynProp == null) return null
    if (js("typeof dynProp === 'number'") as Boolean) return dynProp.unsafeCast<Double>().toInt()
    return (dynProp as? Number)?.toInt()
}

private fun dynamicToLong(dynProp: dynamic): Long? {
    if (dynProp == null) return null
    if (js("typeof dynProp === 'number'") as Boolean) return dynProp.unsafeCast<Double>().toLong()
    return (dynProp as? Number)?.toLong()
}

private fun dynamicToFloat(dynProp: dynamic): Float? {
    if (dynProp == null) return null
    if (js("typeof dynProp === 'number'") as Boolean) return dynProp.unsafeCast<Double>().toFloat()
    return (dynProp as? Number)?.toFloat()
}

private fun dynamicToBoolean(dynProp: dynamic): Boolean? {
    if (dynProp == null) return null
    if (js("typeof dynProp === 'boolean'") as Boolean) return dynProp.unsafeCast<Boolean>()
    return dynProp as? Boolean
}

private fun <T> dynamicToList(dynObj: dynamic): List<T> {
    if (dynObj == null) return emptyList()
    
    if (js("typeof dynObj === 'string'") as Boolean) {
        val str = dynObj as String
        if (str.isBlank()) return emptyList()
        return str.split(",").map { it.trim().unsafeCast<T>() }
    }
    
    val list = mutableListOf<T>()
    val len = dynamicToInt(dynObj.length) ?: 0
    for (i in 0 until len) {
        val item = dynObj[i]
        if (item != null) list.add(item.unsafeCast<T>())
    }
    
    // HEALING LOGIC:
    // If a string was previously shredded into an array of characters and saved to the CRDT,
    // it will be parsed as a list of 1-character strings, including commas.
    // e.g. ["C", "4", ",", " ", "E", "4"]
    if (list.isNotEmpty() && list.all { js("typeof it === 'string'") as Boolean }) {
        val strList = list.unsafeCast<List<String>>()
        if (strList.contains(",")) {
            val healedStr = strList.joinToString("")
            return healedStr.split(",").map { it.trim().unsafeCast<T>() }
        }
    }
    
    return list
}

actual class SongCrdtDoc actual constructor() {
    private var doc: dynamic = Automerge.init()

    private fun updateValue(obj: dynamic, key: String, newValue: Any?) {
        val current = obj[key]
        if (current != newValue) {
            obj[key] = newValue
        }
    }

    actual fun update(song: Song) {
        doc = Automerge.change(doc, "Update from Song object") { d ->
            syncText(d, arrayOf("title"), d.title, song.title)
            syncText(d, arrayOf("notes"), d.notes, song.notes)
            updateValue(d, "guitarCapo", song.guitarCapo)
            updateValue(d, "bpm", song.bpm)
            updateValue(d, "guitarTuning", song.guitarTuning.toTypedArray())
            updateValue(d, "showNotes", song.showNotes)
            updateValue(d, "showPianoInfo", song.showPianoInfo)
            updateValue(d, "showGuitarInfo", song.showGuitarInfo)
            updateValue(d, "ownerId", song.ownerId)
            updateValue(d, "ownerUsername", song.ownerUsername)
            updateValue(d, "collaborators", song.collaborators.toTypedArray())
            updateValue(d, "viewers", song.viewers.toTypedArray())
            updateValue(d, "isPublic", song.isPublic)
            updateValue(d, "isDeleted", song.isDeleted)
            updateValue(d, "lastModified", song.lastModified.toEpochMilliseconds().toDouble())
            if (d.recordings == null) d.recordings = js("{}")
            val existingRecordingKeys = dynamicToList<String>(JsObject.keys(d.recordings))
            existingRecordingKeys.forEach { key -> 
                if (!song.recordings.containsKey(key)) {
                    val reflectDelete: dynamic = js("Reflect.deleteProperty")
                    reflectDelete(d.recordings, key)
                }
            }
            song.recordings.forEach { (key, metaList) ->
                val jsonStr = json.encodeToString(ListSerializer(RecordingMetadata.serializer()), metaList)
                updateValue(d.recordings, key, jsonStr)
            }

            if (d.chordDefinitions == null) d.chordDefinitions = js("{}")
            val existingChordNames = dynamicToList<String>(JsObject.keys(d.chordDefinitions))
            existingChordNames.forEach { name ->
                if (!song.chordDefinitions.containsKey(name)) {
                    val reflectDelete: dynamic = js("Reflect.deleteProperty")
                    reflectDelete(d.chordDefinitions, name)
                }
            }
            song.chordDefinitions.forEach { (name, definition) ->
                if (d.chordDefinitions[name] == null) d.chordDefinitions[name] = js("{}")
                val c = d.chordDefinitions[name]
                updateValue(c, "name", definition.name)
                updateValue(c, "root", definition.root)
                updateValue(c, "quality", definition.quality)
                updateValue(c, "pianoNotes", (definition.pianoNotes ?: emptyList()).toTypedArray())
                updateValue(c, "guitarTab", (definition.guitarTab ?: emptyList()).toTypedArray())
                updateValue(c, "isCustom", definition.isCustom)
                updateValue(c, "lastModified", definition.lastModified.toDouble())
            }
// Studio State
            if (d.studio == null) d.studio = js("{}")
            val studioState = song.studioState ?: songwritersnotebook.app.data.StudioState()
            updateValue(d.studio, "isMetronomeEnabled", studioState.isMetronomeEnabled)
            updateValue(d.studio, "isSnapEnabled", studioState.isSnapEnabled)
            updateValue(d.studio, "bpm", song.bpm)
            updateValue(d.studio, "zoomX", studioState.zoomX)
            updateValue(d.studio, "zoomY", studioState.zoomY)

            if (d.studio.timeSignature == null) d.studio.timeSignature = js("{}")
            updateValue(d.studio.timeSignature, "beats", studioState.timeSignature.beats)
            updateValue(d.studio.timeSignature, "per", studioState.timeSignature.per)

            if (d.studio.snapInterval == null) d.studio.snapInterval = js("{}")
            updateValue(d.studio.snapInterval, "base", studioState.snapInterval.base)
            updateValue(d.studio.snapInterval, "numerator", studioState.snapInterval.numerator)
            updateValue(d.studio.snapInterval, "denominator", studioState.snapInterval.denominator)

            if (d.studio.tracks == null) d.studio.tracks = js("{}")
            val currentTrackIdsInCrdt = dynamicToList<String>(JsObject.keys(d.studio.tracks))
            val newTrackIds = studioState.tracks.map { it.id }.toSet()
            currentTrackIdsInCrdt.forEach { tId -> 
                if (!newTrackIds.contains(tId)) {
                    val reflectDelete: dynamic = js("Reflect.deleteProperty")
                    reflectDelete(d.studio.tracks, tId)
                }
            }

            studioState.tracks.forEach { track ->
                val tId = track.id
                if (d.studio.tracks[tId] == null) d.studio.tracks[tId] = js("{}")
                val t = d.studio.tracks[tId]
                updateValue(t, "name", track.name)
                updateValue(t, "volume", track.volume)
                updateValue(t, "strumSpeed", track.strumSpeed)
                updateValue(t, "position", track.position)
                updateValue(t, "isCollapsed", track.isCollapsed)
                updateValue(t, "lastModified", track.lastModified.toDouble())

                if (t.items == null) t.items = js("{}")
                val currentItemIds = dynamicToList<String>(JsObject.keys(t.items))
                val newItemIds = track.items.map { it.id }.toSet()
                currentItemIds.forEach { iId -> 
                    if (!newItemIds.contains(iId)) {
                        val reflectDelete: dynamic = js("Reflect.deleteProperty")
                        reflectDelete(t.items, iId)
                    }
                }

                track.items.forEach { item ->
                    val iId = item.id
                    if (t.items[iId] == null) t.items[iId] = js("{}")
                    val i = t.items[iId]
                    updateValue(i, "beat", item.beat)
                    updateValue(i, "duration", item.duration)
                    updateValue(i, "type", item.type)
                    updateValue(i, "position", item.position)
                    updateValue(i, "trimStartBeats", item.trimStartBeats)
                    updateValue(i, "lastModified", item.lastModified.toDouble())
                    if (item.originalDurationBeats != null) updateValue(i, "originalDurationBeats", item.originalDurationBeats)
                    updateValue(i, "data", json.encodeToString(TrackData.serializer(), item.data))
                }
            }

            if (d.sections == null) d.sections = js("{}")
            val existingSectionIds = dynamicToList<String>(JsObject.keys(d.sections))
            val newSectionIds = song.sections.map { it.id }.toSet()
            existingSectionIds.forEach { sId ->
                if (!newSectionIds.contains(sId)) {
                    val reflectDelete: dynamic = js("Reflect.deleteProperty")
                    reflectDelete(d.sections, sId)
                }
            }

            song.sections.forEach { section ->
                val sId = section.id
                if (d.sections[sId] == null) d.sections[sId] = js("{}")
                val s = d.sections[sId]
                syncText(d, arrayOf("sections", sId, "title"), s.title, section.title)
                updateValue(s, "position", section.position)
                updateValue(s, "lastModified", section.lastModified.toDouble())
                updateValue(s, "tabs", json.encodeToString(ListSerializer(GuitarTab.serializer()), section.tabs))

                if (s.lines == null) s.lines = js("{}")
                val existingLineIds = dynamicToList<String>(JsObject.keys(s.lines))
                val newLineIds = section.lines.map { it.id }.toSet()
                existingLineIds.forEach { lId ->
                    if (!newLineIds.contains(lId)) {
                        val reflectDelete: dynamic = js("Reflect.deleteProperty")
                        reflectDelete(s.lines, lId)
                    }
                }

                section.lines.forEach { line ->
                    val lId = line.id
                    if (s.lines[lId] == null) s.lines[lId] = js("{}")
                    val l = s.lines[lId]
                    
                    // Character-level sync for text using splice
                    syncText(d, arrayOf("sections", sId, "lines", lId, "text"), l.text, line.text)
                    
                    updateValue(l, "position", line.position)
                    updateValue(l, "lastModified", line.lastModified.toDouble())
                    updateValue(l, "tabs", json.encodeToString(ListSerializer(GuitarTab.serializer()), line.tabs))
                }
            }
        }
    }

    private fun syncText(doc: dynamic, path: Array<Any>, currentObj: dynamic, newText: String) {
        if (currentObj == null || js("typeof currentObj === 'string'") as Boolean) {
            val textObj = Automerge.Text()
            var currentPathObj = doc
            for (i in 0 until path.size - 1) {
                currentPathObj = currentPathObj[path[i]]
            }
            currentPathObj[path.last()] = textObj
            if (newText.isNotEmpty()) {
                Automerge.next.splice(doc, path, 0, 0, newText)
            }
        } else {
            val currentText = extractString(currentObj)
            if (currentText != newText) {
                var prefixLen = 0
                while (prefixLen < currentText.length && prefixLen < newText.length && currentText[prefixLen] == newText[prefixLen]) {
                    prefixLen++
                }
                var suffixLen = 0
                while (suffixLen < currentText.length - prefixLen && suffixLen < newText.length - prefixLen && 
                       currentText[currentText.length - 1 - suffixLen] == newText[newText.length - 1 - suffixLen]) {
                    suffixLen++
                }
                val deleteCount = currentText.length - prefixLen - suffixLen
                val insertText = newText.substring(prefixLen, newText.length - suffixLen)
                
                if (deleteCount > 0 || insertText.isNotEmpty()) {
                    Automerge.next.splice(doc, path, prefixLen, deleteCount, insertText)
                }
            }
        }
    }

    private fun extractString(dynProp: dynamic): String {
        if (dynProp == null) return ""
        if (js("typeof dynProp === 'string'") as Boolean) return dynProp.unsafeCast<String>()
        return try { dynProp.toString().unsafeCast<String>() } catch (e: Exception) { "" }
    }

    actual fun getSong(): Song {
        println("DEBUG_CRDT_TRACE: getSong start")
        val d = doc
        val title = extractString(d.title)
        println("DEBUG_CRDT_TRACE: title=$title")
        val bpm = dynamicToInt(d.bpm) ?: 120
        println("DEBUG_CRDT_TRACE: bpm=$bpm")
        val notes = extractString(d.notes)
        val capo = dynamicToInt(d.guitarCapo) ?: 0
        val tuning = dynamicToList<String>(d.guitarTuning).takeIf { it.isNotEmpty() } ?: listOf("E2", "A2", "D3", "G3", "B3", "E4")
        val showNotes = dynamicToBoolean(d.showNotes) ?: false
        val showPianoInfo = dynamicToBoolean(d.showPianoInfo) ?: false
        val showGuitarInfo = dynamicToBoolean(d.showGuitarInfo) ?: false
        val lastModifiedMs = dynamicToLong(d.lastModified)
        val lastModified = if (lastModifiedMs != null) kotlinx.datetime.Instant.fromEpochMilliseconds(lastModifiedMs) else kotlinx.datetime.Clock.System.now()
        
        val ownerId = extractString(d.ownerId)
        val ownerUsername = extractString(d.ownerUsername)
        val collaborators = dynamicToList<String>(d.collaborators)
        val viewers = dynamicToList<String>(d.viewers)
        println("DEBUG_CRDT_TRACE: collaborators/viewers parsed")
        val isPublic = dynamicToBoolean(d.isPublic) ?: false
        val isDeleted = dynamicToBoolean(d.isDeleted) ?: false

        println("DEBUG_CRDT_TRACE: parsing recordings")
        val recordingsMap = mutableMapOf<String, List<RecordingMetadata>>()
        if (d.recordings != null) {
            val rMap = d.recordings
            dynamicToList<String>(JsObject.keys(rMap)).forEach { key ->
                try {
                    val list = json.decodeFromString(ListSerializer(RecordingMetadata.serializer()), extractString(rMap[key]))
                    recordingsMap[key] = list
                } catch (e: Exception) { /* Ignore */ }
            }
        }


        // Chords reconstruction
        println("DEBUG_CRDT_TRACE: parsing chords")
        val chordsMap = mutableMapOf<String, songwritersnotebook.app.data.ChordDefinition>()
        if (d.chordDefinitions != null) {
            val cMap = d.chordDefinitions
            val names = dynamicToList<String>(JsObject.keys(cMap))
            names.forEach { name ->
                val c = cMap[name]
                if (c != null) {
                    val cName = extractString(c.name).ifEmpty { name }
                    val cRoot = extractString(c.root)
                    val pNotes = dynamicToList<String>(c.pianoNotes)
                    val gTab = dynamicToList<String>(c.guitarTab)
                    val isCustom = dynamicToBoolean(c.isCustom) ?: false
                    val cModified = dynamicToLong(c.lastModified) ?: 0L
                    
                    chordsMap[name] = songwritersnotebook.app.data.ChordDefinition(
                        name = cName,
                        root = cRoot,
                        quality = extractString(c.quality),
                        pianoNotes = pNotes,
                        guitarTab = gTab,
                        isCustom = isCustom,
                        lastModified = cModified
                    )
                }
            }
        }

        println("DEBUG_CRDT_TRACE: parsing sections")
        val sectionsList = mutableListOf<Section>()
        if (d.sections != null) {
            val sectionsMap = d.sections
            val sIds = dynamicToList<String>(JsObject.keys(sectionsMap))
            sIds.forEach { sId ->
                val sObj = sectionsMap[sId]
                if (sObj != null) {
                    val sTitle = extractString(sObj.title)
                    val sPos = extractString(sObj.position)
                    val sModified = dynamicToLong(sObj.lastModified) ?: 0L
                    val sTabs = try { json.decodeFromString(ListSerializer(GuitarTab.serializer()), extractString(sObj.tabs)) } catch (e: Exception) { emptyList() }
                    
                    val linesList = mutableListOf<Line>()
                    if (sObj.lines != null) {
                        val linesMap = sObj.lines
                        val lIds = dynamicToList<String>(JsObject.keys(linesMap))
                        lIds.forEach { lId ->
                            val lObj = linesMap[lId]
                            if (lObj != null) {
                                val lText = extractString(lObj.text)
                                val lPos = extractString(lObj.position)
                                val lModified = dynamicToLong(lObj.lastModified) ?: 0L
                                val lTabs = try { json.decodeFromString(ListSerializer(GuitarTab.serializer()), extractString(lObj.tabs)) } catch (e: Exception) { emptyList() }
                                linesList.add(Line(id = lId, text = lText, position = lPos, tabs = lTabs, sectionId = sId, lastModified = lModified))
                            }
                        }
                    }
                    sectionsList.add(Section(id = sId, title = sTitle, position = sPos, tabs = sTabs, lines = linesList.sortedBy { it.position }, lastModified = sModified))
                }
            }
        }

        // Studio reconstruction
        println("DEBUG_CRDT_TRACE: parsing studio")
        var studioState: songwritersnotebook.app.data.StudioState? = null
        if (d.studio != null) {
            val tracksList = mutableListOf<Track>()
            if (d.studio.tracks != null) {
                val tMap = d.studio.tracks
                dynamicToList<String>(JsObject.keys(tMap)).forEach { tId ->
                    val tObj = tMap[tId]
                    val tModified = dynamicToLong(tObj.lastModified) ?: 0L
                    val itemsList = mutableListOf<TrackItem>()
                    if (tObj.items != null) {
                        val iMap = tObj.items
                        dynamicToList<String>(JsObject.keys(iMap)).forEach { iId ->
                            val iObj = iMap[iId]
                            val iModified = dynamicToLong(iObj.lastModified) ?: 0L
                            itemsList.add(TrackItem(
                                id = iId,
                                beat = dynamicToFloat(iObj.beat) ?: 0f,
                                duration = dynamicToFloat(iObj.duration) ?: 1f,
                                type = extractString(iObj.type),
                                position = extractString(iObj.position).takeIf { it.isNotEmpty() } ?: "m",
                                trimStartBeats = dynamicToFloat(iObj.trimStartBeats) ?: 0f,
                                originalDurationBeats = dynamicToFloat(iObj.originalDurationBeats),
                                data = try { json.decodeFromString(TrackData.serializer(), extractString(iObj.data)) } catch (e: Exception) { TrackData() },
                                trackId = tId,
                                lastModified = iModified
                            ))
                        }
                    }
                    tracksList.add(Track(
                        id = tId,
                        name = extractString(tObj.name),
                        volume = dynamicToFloat(tObj.volume) ?: 1f,
                        strumSpeed = dynamicToFloat(tObj.strumSpeed) ?: 1f,
                        position = extractString(tObj.position).takeIf { it.isNotEmpty() } ?: "m",
                        isCollapsed = dynamicToBoolean(tObj.isCollapsed) ?: false,
                        items = itemsList.sortedBy { it.position },
                        lastModified = tModified
                    ))
                }
            }

            val timeSig = if (d.studio.timeSignature != null) {
                TimeSignature(
                    beats = dynamicToInt(d.studio.timeSignature.beats) ?: 4,
                    per = dynamicToInt(d.studio.timeSignature.per) ?: 4
                )
            } else TimeSignature()

            val snapInt = if (d.studio.snapInterval != null) {
                SnapInterval(
                    base = d.studio.snapInterval.base as? String ?: "beat",
                    numerator = dynamicToInt(d.studio.snapInterval.numerator) ?: 1,
                    denominator = dynamicToInt(d.studio.snapInterval.denominator) ?: 4
                )
            } else SnapInterval()

            studioState = songwritersnotebook.app.data.StudioState(
                tracks = tracksList.sortedBy { it.position },
                timeSignature = timeSig,
                isMetronomeEnabled = dynamicToBoolean(d.studio.isMetronomeEnabled) ?: false,
                isSnapEnabled = dynamicToBoolean(d.studio.isSnapEnabled) ?: true,
                snapInterval = snapInt,
                zoomX = dynamicToFloat(d.studio.zoomX) ?: 1.0f,
                zoomY = dynamicToFloat(d.studio.zoomY) ?: 1.0f
            )
        }
        
        println("DEBUG_CRDT_TRACE: parsing heads")
        val headsList = dynamicToList<dynamic>(Automerge.getHeads(doc))
        println("DEBUG_CRDT_TRACE: heads list retrieved")
        val parsedHeads = headsList.map { head ->
            if (js("typeof head === 'string'") as Boolean) {
                head.unsafeCast<String>()
            } else {
                songwritersnotebook.app.utils.Utils.toHex(head.unsafeCast<Uint8Array>().asByteArray())
            }
        }
        println("DEBUG_CRDT_TRACE: heads parsed")

        return Song(
            id = "", // set by repository
            title = title,
            bpm = bpm,
            notes = notes,
            guitarCapo = capo,
            guitarTuning = tuning,
            showNotes = showNotes,
            showPianoInfo = showPianoInfo,
            showGuitarInfo = showGuitarInfo,
            ownerId = ownerId,
            ownerUsername = ownerUsername,
            collaborators = collaborators,
            viewers = viewers,
            isPublic = isPublic,
            isDeleted = isDeleted,
            chordDefinitions = chordsMap,
            sections = sectionsList.sortedBy { it.position },
            recordings = recordingsMap,
            studioState = studioState,
            lastModified = lastModified,
            heads = parsedHeads,
            lastActorId = Automerge.getActorId(doc)
        )
    }

    actual fun save(): ByteArray = Automerge.save(doc).asByteArray()

    actual fun load(data: ByteArray) {
        println("DEBUG_CRDT_TRACE: load start")
        val uint8 = data.asUint8Array()
        println("DEBUG_CRDT_TRACE: load asUint8Array done")
        try {
            doc = Automerge.load(uint8)
        } catch (e: dynamic) {
            println("DEBUG_CRDT_TRACE: RAW JS ERROR: " + JSON.stringify(e))
            js("console.error(e)")
            val msg = try { e.message as String } catch (_: Exception) { e.toString() }
            throw Exception("JS Automerge Load Error: $msg")
        }
        println("DEBUG_CRDT_TRACE: load Automerge.load done")
    }

    actual fun applyChanges(changes: ByteArray) {
        val changesArray = js("[]")
        changesArray.push(changes.asUint8Array())
        try {
            val result = Automerge.applyChanges(doc, changesArray)
            doc = result[0]
        } catch (e: dynamic) {
            val msg = try { e.message as String } catch (_: Exception) { e.toString() }
            throw Exception("JS Automerge ApplyChanges Error: $msg")
        }
    }

    actual fun getChangesSince(lastState: ByteArray?): ByteArray {
        return Automerge.save(doc).asByteArray()
    }

    actual fun merge(otherData: ByteArray) {
        try {
            val otherDoc = Automerge.load(otherData.asUint8Array())
            doc = Automerge.merge(doc, otherDoc)
        } catch (e: dynamic) {
            val msg = try { e.message as String } catch (_: Exception) { e.toString() }
            throw Exception("JS Automerge Merge Error: $msg")
        }
    }

    actual fun getHeads(): List<ByteArray> {
        val headsList = dynamicToList<dynamic>(Automerge.getHeads(doc))
        return headsList.map { head ->
            if (js("typeof head === 'string'") as Boolean) {
                songwritersnotebook.app.utils.Utils.fromHex(head.unsafeCast<String>())
            } else {
                head.unsafeCast<Uint8Array>().asByteArray()
            }
        }
    }

    actual fun getHeadsHashes(): List<String> {
        val headsList = dynamicToList<dynamic>(Automerge.getHeads(doc))
        return headsList.map { head ->
            if (js("typeof head === 'string'") as Boolean) {
                head.unsafeCast<String>()
            } else {
                songwritersnotebook.app.utils.Utils.toHex(head.unsafeCast<Uint8Array>().asByteArray())
            }
        }
    }

    actual fun getActorId(): String {
        return Automerge.getActorId(doc)
    }

    // --- PRO COLLABORATION: Atomic Splicing ---

    actual fun spliceLineText(sectionId: String, lineId: String, index: Int, deleteCount: Int, insertText: String) {
        doc = Automerge.change(doc, "Atomic Line Splice") { d ->
            Automerge.next.splice(d, arrayOf("sections", sectionId, "lines", lineId, "text"), index, deleteCount, insertText)
            d.lastModified = kotlinx.datetime.Clock.System.now().toEpochMilliseconds().toDouble()
        }
    }

    actual fun spliceSectionTitle(sectionId: String, index: Int, deleteCount: Int, insertText: String) {
        doc = Automerge.change(doc, "Atomic Section Title Splice") { d ->
            Automerge.next.splice(d, arrayOf("sections", sectionId, "title"), index, deleteCount, insertText)
            d.lastModified = kotlinx.datetime.Clock.System.now().toEpochMilliseconds().toDouble()
        }
    }

    actual fun spliceSongTitle(index: Int, deleteCount: Int, insertText: String) {
        doc = Automerge.change(doc, "Atomic Song Title Splice") { d ->
            Automerge.next.splice(d, arrayOf("title"), index, deleteCount, insertText)
            d.lastModified = kotlinx.datetime.Clock.System.now().toEpochMilliseconds().toDouble()
        }
    }

    actual fun spliceSongNotes(index: Int, deleteCount: Int, insertText: String) {
        doc = Automerge.change(doc, "Atomic Song Notes Splice") { d ->
            Automerge.next.splice(d, arrayOf("notes"), index, deleteCount, insertText)
            d.lastModified = kotlinx.datetime.Clock.System.now().toEpochMilliseconds().toDouble()
        }
    }
}

fun ByteArray.asUint8Array(): Uint8Array {
    val array = Uint8Array(this.size)
    val d = array.asDynamic()
    for (i in 0 until this.size) {
        d[i] = this[i]
    }
    return array
}

fun Uint8Array.asByteArray(): ByteArray {
    val array = ByteArray(this.length)
    val d = this.asDynamic()
    for (i in 0 until this.length) {
        val num = d[i]
        if (js("typeof num === 'number'") as Boolean) {
            array[i] = num.unsafeCast<Double>().toInt().toByte()
        } else {
            array[i] = (num as Number).toByte()
        }
    }
    return array
}
